import {
  useGetUserAllowedOrganizationOfferGroups,
  useGetUserAllowedOrganizationTags,
} from '@/api/trafikportalen';
import {
  PRODUCT_CLASS_PREFIX,
  TRAVELLER_CLASS_PREFIX,
} from '@/helpers/ticketClass';
import { useAuth } from '@/stores/auth';

/**
 * Returns whether the current user can buy tickets.
 * A user can buy tickets if all of the following are true:
 * - They have write access to at least one department.
 * - The organization has at least one allowed tag or offer group configured.
 * Returns undefined while any of the required information is still loading.
 */
export const useCanBuyTickets = (enabled = true): boolean | undefined => {
  const departmentAccess = useAuth((s) => s.departmentAccess);

  const { data: allowedTags, isLoading: isLoadingTags } =
    useGetUserAllowedOrganizationTags({ query: { enabled } });
  const { data: allowedOfferGroups, isLoading: isLoadingOfferGroups } =
    useGetUserAllowedOrganizationOfferGroups({
      query: { enabled },
    });

  if (!departmentAccess || isLoadingTags || isLoadingOfferGroups) {
    return undefined;
  }

  if (!allowedTags || !allowedOfferGroups) return undefined;

  const masterTags = allowedTags.filter(
    (tag) => tag.collection_type === 'master',
  );
  const hasTravellerClass = masterTags.some((tag) =>
    tag.tag_id.startsWith(TRAVELLER_CLASS_PREFIX),
  );
  const hasProductClass = masterTags.some((tag) =>
    tag.tag_id.startsWith(PRODUCT_CLASS_PREFIX),
  );

  return (
    departmentAccess.editor_departments > 0 &&
    hasTravellerClass &&
    ((masterTags.length > 0 && hasProductClass) ||
      allowedOfferGroups.length > 0)
  );
};
